import { useEffect } from 'react';

// Every node this component owns is stamped so that the next route can clear
// its own tags without disturbing the static tags shipped in public/index.html.
const MANAGED = 'data-seo-managed';

/**
 * Per route head tags for a client side rendered app, with no extra dependency.
 *
 * This is what react-helmet does internally minus the server rendering half,
 * which a CRA build cannot use anyway. Search engines that execute JavaScript
 * read the head after this effect runs. Crawlers that do not execute JavaScript
 * still get the site wide defaults from public/index.html, which is why the
 * static shell carries a full Organization and LocalBusiness graph.
 */
const Seo = ({
  title,
  description,
  canonical,
  keywords,
  image,
  ogType = 'website',
  jsonLd = [],
}) => {
  useEffect(() => {
    const head = document.head;

    // Update in place when the static shell already has the tag, otherwise
    // create it and stamp it as ours. Updating avoids two competing values.
    const meta = (attr, key, content) => {
      if (!content) return;
      let el = head.querySelector('meta[' + attr + '="' + key + '"]');
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        el.setAttribute(MANAGED, '');
        head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    if (title) document.title = title;
    meta('name', 'description', description);
    meta('name', 'keywords', keywords);
    meta('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1');

    meta('property', 'og:type', ogType);
    meta('property', 'og:title', title);
    meta('property', 'og:description', description);
    meta('property', 'og:url', canonical);
    meta('property', 'og:site_name', 'AXIOM SQUARE Business Consultants');
    meta('property', 'og:locale', 'en_PK');
    meta('property', 'og:image', image);

    meta('name', 'twitter:card', 'summary_large_image');
    meta('name', 'twitter:title', title);
    meta('name', 'twitter:description', description);
    meta('name', 'twitter:image', image);

    if (canonical) {
      let link = head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute(MANAGED, '');
        head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Structured data is fully replaced on every route change. Only blocks we
    // added are removed, so the site wide graph in index.html survives.
    var stale = head.querySelectorAll('script[type="application/ld+json"][' + MANAGED + ']');
    Array.prototype.forEach.call(stale, function (n) { n.remove(); });

    jsonLd.filter(Boolean).forEach(function (block) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute(MANAGED, '');
      s.textContent = JSON.stringify(block);
      head.appendChild(s);
    });
  }, [title, description, canonical, keywords, image, ogType, jsonLd]);

  return null;
};

export default Seo;
